import {customElement, query, property} from 'lit/decorators.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '#lit/types/classes.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import type {BoundingBox} from '#lit/lib/bezier.js';
import type {Point} from '#lit/lib/point.js';
import CanvasElement from '../canvas_element/index.js';
import type CanvasGroup from '../canvas_group/index.js';
import type CanvasConnection from '../canvas_connection/index.js';
import {CANVAS_CONNECTION_ELEMENT_TAG} from '../canvas_connection/index.js';
import styles from './style.scss';
import {CanvasWalker} from '#lit/lib/canvas_walker.js';
import {CanvasConnectionEventAction} from '../canvas_connection/architecture.js';
import {getNodeFromPath} from './utils.js';
import {
  DEFAULT_OUTPUT_LIMIT,
  CANVAS_NODE_BORDER_WIDTH,
  NODE_TRACKER_WIDTH,
} from './constants.js';

export const CANVAS_NODE_ELEMENT_TAG = 'fly-canvas-node';

/**
 * Node element to use as a base when interacting with a <fly-canvas>.
 */
@customElement(CANVAS_NODE_ELEMENT_TAG)
export default class CanvasNode extends ImmutableComponent<
  Constructor<CanvasElement>,
  Model,
  Msg
>({component: CanvasElement, main}) {
  private _canvasType = 'node';

  @property({type: Number}) outputLimit: number = DEFAULT_OUTPUT_LIMIT;
  @property({type: Object}) connection?: HTMLElement;
  @property({type: Object}) outputs: Record<number, HTMLElement> = {};
  @property({type: Array}) inputs: HTMLElement[] = [];

  @query('.canvas-node') $node?: HTMLElement;

  walker: CanvasWalker = new CanvasWalker(this);

  static get styles() {
    return [styles];
  }

  static isInPath(composedPath: EventTarget[]): boolean {
    const node = getNodeFromPath(composedPath);
    if (!node) return false;

    /** This should be smart and eventually include an allow list of elements. */
    return true;
  }

  get $group(): CanvasGroup | undefined {
    return this.model.group as CanvasGroup;
  }

  set $group(group: CanvasGroup | undefined) {
    this.handleUpdate(Msg.setGroup(group as HTMLElement));
  }

  @property({type: Object})
  get group(): CanvasGroup | undefined {
    return this.model.group as CanvasGroup;
  }
  set group(group: CanvasGroup | undefined) {
    this.handleUpdate(Msg.setGroup(group as HTMLElement));
  }

  get boundingBox(): BoundingBox {
    return {
      x: this.offsetLeft ?? 0,
      y: this.offsetTop ?? 0,
      width: this.clientWidth ?? 0,
      height: this.clientHeight ?? 0,
    };
  }

  get canvasType(): string {
    return this._canvasType;
  }

  protected canCreateConnection(): boolean {
    return !!this.$canvas;
  }

  hasGroup(): boolean {
    return !!this.$group;
  }

  selectNode() {
    this.handleUpdate(Msg.select());
  }

  setupConnectionElement(connection: CanvasConnection) {
    this.$canvas?.appendChild(connection);
    connection.attach(this.$canvas!);

    this.handleUpdate(Msg.setOutput([connection.connectorIndex, connection]));

    connection.addEventListener(
      CanvasConnectionEventAction.Connected,
      () => this.rerender(),
      {once: true}
    );

    connection.addEventListener('destroyed', () => {
      this.handleUpdate(Msg.removeOutput(connection));
    });
  }

  createConnectionElement() {
    return document.createElement(CANVAS_CONNECTION_ELEMENT_TAG);
  }

  hasValidConnection({index}: {index: number}): boolean {
    return !!(
      this.model.outputs[index] &&
      (this.model.outputs[index] as CanvasConnection).model.connected
    );
  }

  connectorBoundingBox({index}: {index: number}) {
    const wrapper = this.shadowRoot?.querySelector(
      `#connector-wrapper-${index}`
    );
    if (!wrapper || !(wrapper instanceof HTMLElement)) return;

    const nodeConnector = wrapper.querySelector('.canvas-node__connector');
    if (!nodeConnector || !(nodeConnector instanceof HTMLElement)) return;

    const groupOffset = this.$group?.getNodePosition(this.key);

    const nodeOffsetLeft = this.offsetLeft;
    const connectorIndexOffsetLeft = wrapper.clientWidth * index;
    const nodeConnectorOffsetLeft = Math.round(
      nodeConnector.offsetLeft + nodeConnector.clientWidth / 2
    );

    const nodeOffsetTop = Math.floor(
      this.offsetTop - nodeConnector.clientHeight
    );

    const baseOffsetLeft = groupOffset?.x ?? nodeOffsetLeft;
    const baseOffsetTop = groupOffset?.y ?? nodeOffsetTop;

    const x =
      baseOffsetLeft + connectorIndexOffsetLeft + nodeConnectorOffsetLeft;

    const y = baseOffsetTop + this.clientHeight;

    return {
      x,
      y,
      width: nodeConnector.clientWidth ?? 0,
      height: nodeConnector.clientHeight ?? 0,
    };
  }

  setSocketPosition() {
    this.style.setProperty(
      '--tracker-position',
      `${this.calcSocketPosition()}px`
    );

    this.style.setProperty(
      '--tracker-display',
      this.calcSocketPosition() === 0 ? 'none' : 'block'
    );
  }

  calcSocketPosition(): number {
    if (!this.$canvas) return 0;
    const socket = this.socketOffset();
    const nodeOffset = this.offsetLeft;
    const spacingOffset = CANVAS_NODE_BORDER_WIDTH;
    const trackerWidth = NODE_TRACKER_WIDTH;

    return socket.x - nodeOffset - spacingOffset - trackerWidth / 2;
  }

  connect(connection: CanvasConnection) {
    this.walker.clear();

    this.handleUpdate(Msg.addInput(connection));

    connection.addEventListener('destroyed', () => {
      this.disconnect(connection);
    });
  }

  disconnect(connection: CanvasConnection) {
    this.handleUpdate(Msg.removeInput(connection));
    this.walker.clear();
  }

  createConnection(params: {index: number}) {
    if (!this.canCreateConnection()) return;

    if (this.model.outputs[params.index]) {
      (this.model.outputs[params.index] as CanvasConnection).delete();
    }

    const connection = this.createConnectionElement();
    connection.source = this;
    connection.connectorIndex = params.index;
    this.setupConnectionElement(connection);
    return connection;
  }

  destroyConnection({index}: {index: number}) {
    if (!this.model.outputs[index]) return;

    const connection = this.model.outputs[index] as CanvasConnection;
    connection.delete();

    this.detachOutput(index);
  }

  detachOutputs() {
    this.handleUpdate(Msg.removeAllOutputs());
  }

  detachOutput(target: number | HTMLElement) {
    if (typeof target === 'number') {
      return this.handleUpdate(Msg.removeOutputByIndex(target));
    }

    this.handleUpdate(Msg.removeOutput(target));
  }

  delete() {
    deleteOutput(
      Object.values(this.model.outputs),
      Array.from(this.model.inputs)
    );

    super.delete();
  }

  move(by: Point) {
    // Disable moving if the node has a group.
    if (this.$group) return this;
    return super.move(by);
  }

  root() {
    return this.$node ?? null;
  }
}

export function deleteOutput(outputs: HTMLElement[], inputs: HTMLElement[]) {
  if (outputs.length) {
    outputs.forEach(output => {
      (output as CanvasConnection).delete();
    });
  }

  if (inputs.length) {
    inputs.forEach(input => {
      (input as CanvasConnection).delete();
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CANVAS_NODE_ELEMENT_TAG]: CanvasNode;
  }
}
